import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  X, GitPullRequest, MessageCircle, Clock, ExternalLink, Wrench, Bug,
  Loader2, CheckCircle2, GitMerge, AlertCircle, FileCode2, ChevronDown, Trash2,
} from "lucide-react";
import { api } from "../../lib/api";
import { SeverityBadge, CategoryTag } from "./ui";

export default function DiagnosticDrawer({ signalId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pr, setPr] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [opening, setOpening] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [showFile, setShowFile] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true); setData(null); setPr(null);
    api.get(`/friction/${signalId}`)
      .then((r) => { if (active) { setData(r.data); setPr(r.data.pr || null); if (r.data.pr?.status === "opening") startPolling(); } })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const startPolling = () => {
    setOpening(true);
    let tries = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/friction/${signalId}`);
        const p = r.data.pr;
        if (p && p.status === "opened") {
          clearInterval(pollRef.current); setOpening(false); setPr(p);
          toast.success(`PR #${p.pr_number} opened on GitHub`); onChanged && onChanged();
        } else if (p && p.status === "failed") {
          clearInterval(pollRef.current); setOpening(false); setPr(p);
          toast.error(p.error || "PR open failed");
        }
      } catch (e) { /* keep polling */ }
      if (tries > 20) { clearInterval(pollRef.current); setOpening(false); }
    }, 2000);
  };

  const prepare = async () => {
    setPreparing(true);
    try {
      const res = await api.post(`/friction/${signalId}/pr/draft`);
      setPr(res.data); setShowFile(false);
      toast.success("Pull request prepared (dry-run). Review before opening.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prepare PR");
    } finally { setPreparing(false); }
  };

  const openPr = async () => {
    setOpening(true);
    try {
      const res = await api.post(`/friction/${signalId}/pr/open`);
      if (res.data?.status === "opened") {
        setPr((p) => ({ ...p, ...res.data })); setOpening(false);
        toast.success(`PR #${res.data.pr_number} opened`); onChanged && onChanged();
      } else {
        setPr((p) => ({ ...p, status: "opening" }));
        startPolling();
      }
    } catch (e) {
      setOpening(false);
      toast.error(e?.response?.data?.detail || "Could not open PR");
    }
  };

  const discard = async () => {
    setDiscarding(true);
    try {
      await api.delete(`/friction/${signalId}/pr`);
      setPr(null); setShowFile(false);
      toast.success("Draft discarded");
      onChanged && onChanged();
    } catch (e) {
      toast.error("Could not discard draft");
    } finally { setDiscarding(false); }
  };

  const d = data?.diagnostic;
  const status = pr?.status;

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-50 bg-foreground/40"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
        <motion.aside onClick={(e) => e.stopPropagation()}
          initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.25 }}
          className="absolute right-0 top-0 h-full w-full max-w-2xl bg-background border-l-2 border-foreground overflow-y-auto">
          <div className="sticky top-0 bg-foreground text-background flex items-center justify-between px-5 py-3 z-10">
            <span className="font-mono text-xs uppercase tracking-widest flex items-center gap-2">
              <Bug size={14} className="text-accent" /> Diagnostic Parser
            </span>
            <button onClick={onClose} data-testid="diagnostic-close-btn" className="hover:text-accent transition-colors"><X size={20} /></button>
          </div>

          {loading || !data ? (
            <div className="p-10 font-mono text-sm text-muted-foreground flex items-center gap-2">
              <span className="w-2 h-2 bg-primary live-dot" /> Reproducing failure path…
            </div>
          ) : (
            <div className="p-6 space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <SeverityBadge severity={data.severity} />
                  <CategoryTag category={data.error_category} />
                </div>
                <h2 className="font-display font-extrabold tracking-tight text-2xl leading-tight">{data.title}</h2>
                <div className="flex items-center gap-4 mt-3 font-mono text-xs text-muted-foreground flex-wrap">
                  <span>{data.repo_full_name} #{data.issue_number}</span>
                  <span className="flex items-center gap-1"><MessageCircle size={12} /> {data.comments}</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {data.age_days}d</span>
                  <a href={data.html_url} target="_blank" rel="noreferrer" data-testid="diagnostic-github-link"
                    className="flex items-center gap-1 text-secondary hover:text-primary transition-colors">
                    <ExternalLink size={12} /> view on GitHub
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-px bg-border border border-border">
                <div className="bg-background p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Est. time-to-value</div>
                  <div className="font-display font-black text-2xl">{d.estimated_ttv_minutes}m</div>
                </div>
                <div className="bg-background p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Hours lost</div>
                  <div className="font-display font-black text-2xl">{data.estimated_hours_lost}h</div>
                </div>
              </div>

              {d.extracted_signal && (
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Isolated signal</div>
                  <pre className="bg-foreground text-background p-4 font-mono text-xs overflow-x-auto whitespace-pre-wrap border-2 border-foreground">{d.extracted_signal}</pre>
                </div>
              )}

              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Root cause</div>
                <p className="font-mono text-sm leading-relaxed">{d.root_cause}</p>
              </div>

              <div className="border-2 border-foreground bg-card">
                <div className="bg-accent text-foreground px-4 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2">
                  <Wrench size={13} /> {d.fix_title}
                </div>
                <ol className="p-4 space-y-2 list-decimal list-inside font-mono text-sm">
                  {d.steps.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
                </ol>
                <pre className="bg-foreground text-background p-4 font-mono text-xs overflow-x-auto whitespace-pre-wrap border-t-2 border-foreground">{d.suggested_patch}</pre>
              </div>

              {/* Zero-Handoff delivery */}
              <div className="border-2 border-foreground bg-secondary text-secondary-foreground">
                <div className="px-4 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2 border-b border-secondary-foreground/20">
                  <GitPullRequest size={13} className="text-accent" /> Zero-Handoff delivery
                </div>
                <div className="p-4 space-y-3">
                  {status === "opened" && pr?.pr_url ? (
                    <a href={pr.pr_url} target="_blank" rel="noreferrer" data-testid="diagnostic-pr-link"
                      className="btn btn-accent btn-block">
                      <CheckCircle2 size={16} /> PR #{pr.pr_number} opened on GitHub <ExternalLink size={14} />
                    </a>
                  ) : status === "opening" || opening ? (
                    <div className="flex items-center gap-2 bg-secondary-foreground/5 px-4 py-3 border-2 border-foreground font-mono text-sm">
                      <Loader2 size={16} className="animate-spin text-accent" /> Opening pull request on GitHub…
                    </div>
                  ) : pr ? (
                    <>
                      <div className="font-mono text-xs space-y-1.5 text-secondary-foreground/80">
                        <div className="flex items-center gap-2"><GitMerge size={13} className="text-accent" /> branch <span className="text-accent">{pr.branch_name}</span></div>
                        <div className="flex items-center gap-2"><FileCode2 size={13} className="text-accent" /> adds <span className="text-accent">{pr.file_path}</span></div>
                      </div>
                      {status === "failed" && pr.error && (
                        <div className="flex items-start gap-2 font-mono text-[11px] text-secondary-foreground bg-primary/15 p-3 border border-primary">
                          <AlertCircle size={14} className="text-primary shrink-0 mt-0.5" /> {pr.error}
                        </div>
                      )}
                      <button onClick={() => setShowFile((v) => !v)} data-testid="diagnostic-toggle-file"
                        className="flex items-center gap-1 font-mono text-[11px] text-secondary-foreground/60 hover:text-accent transition-colors">
                        <ChevronDown size={12} className={showFile ? "rotate-180 transition-transform" : "transition-transform"} /> {showFile ? "hide" : "preview"} PR file
                      </button>
                      {showFile && (
                        <pre className="bg-foreground text-background p-3 font-mono text-[11px] overflow-x-auto whitespace-pre-wrap max-h-56 border border-foreground">{pr.file_content}</pre>
                      )}
                      <div className="flex items-center gap-2">
                        {!pr.pr_delivery ? (
                          <div className="flex-1 flex items-start gap-2 font-mono text-[11px] text-secondary-foreground/80 bg-primary/10 p-3 border border-primary" data-testid="diagnostic-upgrade-note">
                            <AlertCircle size={14} className="text-primary shrink-0 mt-0.5" />
                            Programmatic PR delivery is a <span className="text-accent font-bold">Solo Maintainer</span> feature. Upgrade in Settings → Billing to ship fixes as Pull Requests.
                          </div>
                        ) : pr.push_access && pr.token_configured ? (
                          <button onClick={openPr} disabled={opening} data-testid="diagnostic-open-pr-btn"
                            className="btn btn-primary flex-1">
                            <GitPullRequest size={16} /> {status === "failed" ? "Retry Open PR" : "Open Pull Request"}
                          </button>
                        ) : (
                          <div className="flex-1 flex items-start gap-2 font-mono text-[11px] text-secondary-foreground/70 bg-secondary-foreground/5 p-3 border border-secondary-foreground/20">
                            <AlertCircle size={14} className="text-accent shrink-0 mt-0.5" />
                            {pr.token_configured
                              ? "Dry-run only — your token has no push access to this repository (auto-fork is disabled)."
                              : "Dry-run only — no GitHub token configured on the server."}
                          </div>
                        )}
                        <button onClick={discard} disabled={discarding} data-testid="diagnostic-discard-pr-btn"
                          title="Discard draft" className="btn btn-outline btn-icon">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-mono text-xs text-secondary-foreground/70">
                        Prepares a branch + advisory file + PR body. Nothing is pushed until you review and open it.
                        Recommended channel: <span className="text-accent">{d.delivery_channel}</span>.
                      </p>
                      <button onClick={prepare} disabled={preparing} data-testid="diagnostic-prepare-pr-btn"
                        className="btn btn-accent btn-block">
                        {preparing ? <Loader2 size={16} className="animate-spin" /> : <GitPullRequest size={16} />} Prepare Pull Request (dry-run)
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.aside>
      </motion.div>
    </AnimatePresence>
  );
}
